import React, { useState, useEffect } from 'react';
import { facturasAPI, obrasAPI, clientesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Search, MoreVertical, Pencil, Trash2, Receipt,
  Download, Send, CheckCircle, X, Copy
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor, translateEstado, getTodayISO, addDaysToDate } from '../lib/utils';

const FacturasPage = () => {
  const [facturas, setFacturas] = useState([]);
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [formData, setFormData] = useState({
    obra_id: '',
    cliente_id: '',
    concepto: '',
    items: [],
    subtotal: 0,
    iva: 21,
    total: 0,
    estado: 'pendiente',
    fecha_emision: getTodayISO(),
    fecha_vencimiento: addDaysToDate(getTodayISO(), 30),
    notas: '',
  });
  const [newItem, setNewItem] = useState({ descripcion: '', cantidad: 1, precio_unitario: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [facturasRes, obrasRes, clientesRes] = await Promise.all([
        facturasAPI.list(),
        obrasAPI.list(),
        clientesAPI.list(),
      ]);
      setFacturas(facturasRes.data);
      setObras(obrasRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items, iva) => {
    const subtotal = items.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);
    const total = subtotal * (1 + iva / 100);
    return { subtotal, total };
  };

  const addItem = () => {
    if (!newItem.descripcion || newItem.precio_unitario <= 0) {
      toast.error('Completa la descripción y precio del item');
      return;
    }
    const updatedItems = [...formData.items, { ...newItem }];
    const totals = calculateTotals(updatedItems, formData.iva);
    setFormData({ ...formData, items: updatedItems, ...totals });
    setNewItem({ descripcion: '', cantidad: 1, precio_unitario: 0 });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(updatedItems, formData.iva);
    setFormData({ ...formData, items: updatedItems, ...totals });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedFactura) {
        await facturasAPI.update(selectedFactura.id, formData);
        toast.success('Factura actualizada');
      } else {
        await facturasAPI.create(formData);
        toast.success('Factura creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar factura');
    }
  };

  const handleEdit = (factura) => {
    setSelectedFactura(factura);
    setFormData({
      obra_id: factura.obra_id || '',
      cliente_id: factura.cliente_id || '',
      concepto: factura.concepto || '',
      items: factura.items || [],
      subtotal: factura.subtotal || 0,
      iva: factura.iva || 21,
      total: factura.total || 0,
      estado: factura.estado || 'pendiente',
      fecha_emision: factura.fecha_emision || getTodayISO(),
      fecha_vencimiento: factura.fecha_vencimiento || addDaysToDate(getTodayISO(), 30),
      notas: factura.notas || '',
    });
    setDialogOpen(true);
  };

  const handleChangeEstado = async (factura, nuevoEstado) => {
    try {
      await facturasAPI.updateEstado(factura.id, nuevoEstado);
      toast.success(`Estado cambiado a ${translateEstado(nuevoEstado)}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    try {
      await facturasAPI.delete(selectedFactura.id);
      toast.success('Factura eliminada');
      setDeleteDialogOpen(false);
      setSelectedFactura(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar factura');
    }
  };

  const handleDownloadPDF = async (factura) => {
    try {
      toast.loading('Generando PDF...', { id: 'pdf-download' });
      await facturasAPI.downloadPDF(factura.id, `factura_${factura.numero}.pdf`);
      toast.success('PDF descargado correctamente', { id: 'pdf-download' });
    } catch (error) {
      toast.error('Error al descargar el PDF', { id: 'pdf-download' });
    }
  };

  const handleDuplicar = async (factura) => {
    try {
      const response = await facturasAPI.duplicar(factura.id);
      toast.success(`Factura duplicada: ${response.data.numero}`);
      loadData();
    } catch (error) {
      toast.error('Error al duplicar la factura');
    }
  };

  const resetForm = () => {
    setSelectedFactura(null);
    setFormData({
      obra_id: '',
      cliente_id: '',
      concepto: '',
      items: [],
      subtotal: 0,
      iva: 21,
      total: 0,
      estado: 'pendiente',
      fecha_emision: getTodayISO(),
      fecha_vencimiento: addDaysToDate(getTodayISO(), 30),
      notas: '',
    });
    setNewItem({ descripcion: '', cantidad: 1, precio_unitario: 0 });
  };

  const getClienteName = (clienteId) => clientes.find((c) => c.id === clienteId)?.nombre || 'Desconocido';
  const getObraName = (obraId) => obras.find((o) => o.id === obraId)?.titulo || 'Sin obra';

  const filteredFacturas = facturas.filter(
    (f) =>
      f.numero?.toLowerCase().includes(search.toLowerCase()) ||
      f.concepto?.toLowerCase().includes(search.toLowerCase()) ||
      getClienteName(f.cliente_id)?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="facturas-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Facturas</h1>
          <p className="text-slate-500 text-sm mt-1">{facturas.length} facturas emitidas</p>
        </div>
        <div className="lg:hidden flex-1">
          <p className="text-xs text-slate-500">{facturas.length} facturas</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800 h-10 lg:h-9 active:scale-95 transition-transform"
          data-testid="new-factura-btn"
        >
          <Plus className="w-4 h-4 mr-1 lg:mr-2" />
          <span className="hidden sm:inline">Nueva Factura</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar facturas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 lg:h-10 lg:max-w-md"
          data-testid="search-facturas-input"
        />
      </div>

      {/* Content */}
      {filteredFacturas.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search ? 'No se encontraron facturas' : 'No hay facturas'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="lg:hidden space-y-3">
            {filteredFacturas.map((factura) => {
              const isOverdue = factura.estado !== 'cobrada' && new Date(factura.fecha_vencimiento) < new Date();
              return (
                <Card 
                  key={factura.id} 
                  className="border-slate-200 active:scale-[0.99] transition-transform"
                  data-testid={`factura-card-${factura.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded ${isOverdue ? 'bg-red-100 text-red-700' : getStatusColor(factura.estado)}`}>
                            {isOverdue ? 'Vencida' : translateEstado(factura.estado)}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">{factura.numero}</span>
                        </div>
                        <p className="font-semibold text-slate-900 truncate">{factura.concepto}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{getClienteName(factura.cliente_id)}</p>
                      </div>
                      <div className="flex flex-col items-end">
                        <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(factura.total)}</p>
                        <p className={`text-[10px] mt-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                          Vence: {formatDate(factura.fecha_vencimiento)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-xs"
                        onClick={() => handleDownloadPDF(factura)}
                        data-testid={`download-pdf-${factura.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-9 w-9" data-testid={`factura-actions-${factura.id}`}>
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(factura)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicar(factura)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          {factura.estado === 'pendiente' && (
                            <DropdownMenuItem onClick={() => handleChangeEstado(factura, 'enviada')}>
                              <Send className="w-4 h-4 mr-2" />
                              Marcar Enviada
                            </DropdownMenuItem>
                          )}
                          {(factura.estado === 'pendiente' || factura.estado === 'enviada') && (
                            <DropdownMenuItem onClick={() => handleChangeEstado(factura, 'cobrada')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marcar Cobrada
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => { setSelectedFactura(factura); setDeleteDialogOpen(true); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <Card className="border-slate-200 hidden lg:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Número</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Concepto</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Cliente</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Total</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Vencimiento</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredFacturas.map((factura) => {
                      const isOverdue = factura.estado !== 'cobrada' && new Date(factura.fecha_vencimiento) < new Date();
                      return (
                        <tr key={factura.id} className="hover:bg-slate-50" data-testid={`factura-row-${factura.id}`}>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-slate-600">{factura.numero}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{factura.concepto}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {getClienteName(factura.cliente_id)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${isOverdue ? 'bg-red-100 text-red-700' : getStatusColor(factura.estado)}`}>
                              {isOverdue ? 'Vencida' : translateEstado(factura.estado)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-sm font-medium">
                            {formatCurrency(factura.total)}
                          </td>
                          <td className={`px-4 py-3 text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                            {formatDate(factura.fecha_vencimiento)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`factura-desktop-actions-${factura.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(factura)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadPDF(factura)}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Descargar PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicar(factura)} data-testid="duplicar-factura-btn">
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplicar
                                </DropdownMenuItem>
                                {factura.estado === 'pendiente' && (
                                  <DropdownMenuItem onClick={() => handleChangeEstado(factura, 'enviada')}>
                                    <Send className="w-4 h-4 mr-2" />
                                    Marcar Enviada
                                  </DropdownMenuItem>
                                )}
                                {(factura.estado === 'pendiente' || factura.estado === 'enviada') && (
                                  <DropdownMenuItem onClick={() => handleChangeEstado(factura, 'cobrada')}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Marcar Cobrada
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => { setSelectedFactura(factura); setDeleteDialogOpen(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="factura-dialog">
          <DialogHeader>
            <DialogTitle>{selectedFactura ? 'Editar Factura' : 'Nueva Factura'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                  required
                >
                  <SelectTrigger data-testid="factura-cliente-select" className="h-12 lg:h-10">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Obra *</Label>
                <Select
                  value={formData.obra_id}
                  onValueChange={(value) => setFormData({ ...formData, obra_id: value })}
                  required
                >
                  <SelectTrigger data-testid="factura-obra-select">
                    <SelectValue placeholder="Seleccionar obra" />
                  </SelectTrigger>
                  <SelectContent>
                    {obras.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Input
                value={formData.concepto}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                placeholder="Descripción de la factura"
                required
                data-testid="factura-concepto-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fecha Emisión</Label>
                <Input
                  type="date"
                  value={formData.fecha_emision}
                  onChange={(e) => setFormData({ ...formData, fecha_emision: e.target.value })}
                  data-testid="factura-emision-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Vencimiento</Label>
                <Input
                  type="date"
                  value={formData.fecha_vencimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                  data-testid="factura-vencimiento-input"
                />
              </div>
              <div className="space-y-2">
                <Label>IVA (%)</Label>
                <Input
                  type="number"
                  value={formData.iva}
                  onChange={(e) => {
                    const iva = parseFloat(e.target.value) || 21;
                    const totals = calculateTotals(formData.items, iva);
                    setFormData({ ...formData, iva, ...totals });
                  }}
                  data-testid="factura-iva-input"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <Label>Partidas</Label>
              <div className="border border-slate-200 rounded-md p-4 space-y-3">
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                        <span className="flex-1">{item.descripcion}</span>
                        <span className="text-slate-500">{item.cantidad} x</span>
                        <span className="font-mono">{formatCurrency(item.precio_unitario)}</span>
                        <span className="font-mono font-medium">{formatCurrency(item.cantidad * item.precio_unitario)}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(index)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    placeholder="Descripción"
                    value={newItem.descripcion}
                    onChange={(e) => setNewItem({ ...newItem, descripcion: e.target.value })}
                    className="flex-1"
                    data-testid="factura-item-descripcion"
                  />
                  <Input
                    type="number"
                    placeholder="Cant."
                    value={newItem.cantidad}
                    onChange={(e) => setNewItem({ ...newItem, cantidad: parseInt(e.target.value) || 1 })}
                    className="w-20"
                    data-testid="factura-item-cantidad"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Precio"
                    value={newItem.precio_unitario || ''}
                    onChange={(e) => setNewItem({ ...newItem, precio_unitario: parseFloat(e.target.value) || 0 })}
                    className="w-28"
                    data-testid="factura-item-precio"
                  />
                  <Button type="button" variant="outline" onClick={addItem} data-testid="factura-add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-3 bg-slate-50 rounded-md space-y-1">
              <div className="text-right space-y-1">
                <p className="text-sm text-slate-500">Subtotal: <span className="font-mono">{formatCurrency(formData.subtotal)}</span></p>
                <p className="text-sm text-slate-500">IVA ({formData.iva}%): <span className="font-mono">{formatCurrency(formData.subtotal * formData.iva / 100)}</span></p>
                <p className="text-lg font-bold text-slate-900">Total: <span className="font-mono">{formatCurrency(formData.total)}</span></p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                data-testid="factura-notas-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-factura-btn">
                {selectedFactura ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar factura?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la factura "{selectedFactura?.numero}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-factura-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FacturasPage;
